import * as React from 'react'
import { api } from '../lib/api'
import Card from '@mui/joy/Card'
import Button from '@mui/joy/Button'
import Input from '@mui/joy/Input'
import Chip from '@mui/joy/Chip'
import { useNavigate } from 'react-router-dom'
import Markdown from 'markdown-to-jsx'
import Navbar from '../Nav'
import Box from '@mui/joy/Box'

interface Job {
  id: string
  title: string
  companyName: string
  location: string
  isRemote: boolean
  tags: string[]
  descriptionMd: string
  maxInterviewMinutes: number
  questions: Question[]
}

interface Question {
  id: string
  qType: 'coding' | 'text'
  promptMd: string
}

export function Jobs() {
  const [jobs, setJobs] = React.useState<Job[]>([])
  const [q, setQ] = React.useState('')
  const [tag, setTag] = React.useState('')
  const [remote, setRemote] = React.useState(false)
  const search = async () => {
    const p = new URLSearchParams()
    if (q) p.set('q', q)
    if (tag) p.set('tags', tag)
    if (remote) p.set('remote', '1')
    const res = await api('/jobs?' + p.toString())
    setJobs(res)
  }
  React.useEffect(() => {
    search()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4">
      <div className="flex gap-2">
        <Input placeholder="Search by keyword…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Input
          placeholder="Tag (e.g. react)"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        />
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={remote} onChange={(e) => setRemote(e.target.checked)} />{' '}
          Remote
        </label>
        <Button onClick={search}>Search</Button>
      </div>
      <div className="grid gap-3">
        {jobs.map((j) => (
          <Card key={j.id} variant="outlined" className="hover:shadow transition">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-lg">{j.title}</div>
                <div className="text-sm opacity-70">
                  {j.companyName} • {j.isRemote ? 'Remote' : j.location || ''}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(j.tags || []).map((t: string) => (
                    <Chip key={t} variant="soft" size="sm">
                      {t}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={'/jobs/' + j.id}>
                  <Button>View</Button>
                </a>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}
export function JobDetail({ id }: { id: string }) {
  const [job, setJob] = React.useState<Job | null>(null)
  const nav = useNavigate()
  React.useEffect(() => {
    api('/jobs/' + id).then(setJob)
  }, [id])
  if (!job) return <div className="p-6">Loading…</div>
  const applyInterview = async () => {
    const app = await api(`/applications/${id}/apply`, { method: 'POST', body: JSON.stringify({}) })
    const session = await api('/interview/sessions', {
      method: 'POST',
      body: JSON.stringify({ applicationId: app.id, capMinutes: job.maxInterviewMinutes }),
    })
    nav('/interview/' + session.id)
  }
  return (
    <Box>
      <Navbar />
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{job.title}</h1>
          <Button onClick={applyInterview}>Apply & Interview</Button>
        </div>
        <div className="text-sm opacity-70">
          {job.location} • {job.isRemote ? 'Remote' : 'On-site'} • Max interview{' '}
          {job.maxInterviewMinutes} min
        </div>
        <div className="flex gap-2 flex-wrap">
          {(job.tags || []).map((t: string) => (
            <Chip key={t} variant="soft" size="sm">
              {t}
            </Chip>
          ))}
        </div>
        <Card variant="outlined" className="prose dark:prose-invert">
          <Markdown>{job.descriptionMd || ''}</Markdown>
        </Card>
        <div>
          <h3 className="font-semibold">Questions & Challenges</h3>
          <ul className="list-disc pl-6">
            {(job.questions || []).map((q: Question) => (
              <li key={q.id}>
                <span className="font-medium">{q.qType === 'coding' ? 'Coding: ' : ''}</span>
                {q.promptMd}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Box>
  )
}
